import { Link } from 'Components/Core';
import { RootState } from 'Data/Redux';
import { hasNonEmptyValue } from 'Lib/Any';
import { isWebSerialApiSupported } from 'Lib/WebSerialApi/helpers';
import classNames from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';
import { FormatTypes } from '../../../Lib/Format';
import { Button, Dialog, Field, TextOutput } from '../../Core';
import InterfaceConnectionButtons from '../Interfacing/InterfaceConnectionButtons';
import EquipmentTypes from '../equipmentTypes';
import './EquipmentDialog.scss';

const isDevMode = process.env.REACT_APP_ENABLE_DEV_MODE === 'true';

type CalibrationStatusProps = {
  overdue?: boolean;
  lastPerformedDate?: any;
  dueDate?: any;
  name?: string;
};

/**
 * Component to display current status of a calibration
 *
 * Can currently come from the status properties or a calibration task model which have a different structure,
 * hence the weird props contract where we pass in everything individually
 */
const CalibrationStatus: React.FC<CalibrationStatusProps> = ({ name, lastPerformedDate, dueDate, overdue }) => (
  <>
    <div className={classNames('EquipmentDialog-Subheading', { 'EquipmentDialog-Subheading-error': overdue })}>
      {name}
      {overdue && ' - Overdue'}
    </div>
    <Field
      label="Last Performed"
      input={<TextOutput />}
      value={lastPerformedDate}
      format={{ type: FormatTypes.Date }}
      horizontal
    />
    <Field label="Due" input={<TextOutput />} value={dueDate} format={{ type: FormatTypes.Date }} horizontal />
  </>
);

interface IEquipmentDialogProps {
  equipment?: any;
  equipmentType: string;
  status?: any;
  portSettings?: any;
  statusDate?: any;
  importAction?: any;
  importModel?: any;
  importedCalibration?: any;
  importedAdditionalData?: any;
  importing?: boolean;
  visible?: boolean;
  onClose: (event?: any) => void;
}

const EquipmentDialog: React.FC<IEquipmentDialogProps> = ({
  equipment,
  equipmentType,
  status,
  statusDate,
  importAction,
  importModel,
  importedCalibration,
  importedAdditionalData,
  importing,
  visible,
  onClose,
}) => {
  const isConnected = useSelector(
    (state: RootState) => state.equipmentInterfacing.equipment[equipment?.Id]?.isConnected ?? false
  );

  return (
    <Dialog
      title={`${EquipmentTypes[equipmentType] ? EquipmentTypes[equipmentType].name + ' ' : ''}${equipment.Code}`}
      width="450px"
      visible={visible}
      onClose={onClose}
      buttons={[
        ...(importModel &&
        ((importedCalibration && importedCalibration.Mismatch === true) ||
          (importedAdditionalData && importedAdditionalData.Mismatch === true))
          ? [
              <Button
                key="Import-Button"
                loading={importing}
                onClick={() => {
                  importAction(importModel);
                  onClose();
                }}
              >
                Re-Import
              </Button>,
            ]
          : []),
        <Button key="Close-Button" onClick={onClose} alwaysEnabled>
          Close
        </Button>,
      ]}
    >
      <div className="EquipmentDialog-Content">
        <div className="EquipmentDialog-Section">
          <Field
            label="Date of Test"
            input={<TextOutput />}
            value={statusDate || new Date()}
            format={{ type: FormatTypes.Date }}
            horizontal
          />
        </div>

        {hasNonEmptyValue(EquipmentTypes[equipmentType]?.additionalDataFields) && (
          <div className="EquipmentDialog-Section">
            <div className="EquipmentDialog-Heading">Properties</div>
            {EquipmentTypes[equipmentType]?.additionalDataFields?.(equipment)}
            {importedAdditionalData && importedAdditionalData.Mismatch && (
              <>
                <div className="EquipmentDialog-Subheading">Configured properties imported to worksheet</div>
                {EquipmentTypes[equipmentType]?.additionalDataFields?.(importedAdditionalData)}
              </>
            )}
          </div>
        )}

        <div className="EquipmentDialog-Section">
          <div className="EquipmentDialog-Heading">Calibration</div>
          {!status.InCalibration && !status.OutOfCalibration && (
            <span>No valid calibration found for the test date.</span>
          )}
          {status.InCalibration && (
            <CalibrationStatus
              name={status.CalibrationName}
              lastPerformedDate={status.CalibrationDate}
              dueDate={status.CalibrationDueDate}
            />
          )}

          {hasNonEmptyValue(EquipmentTypes[equipmentType]?.calibration) && (
            <>
              {EquipmentTypes[equipmentType]?.calibration?.(equipment)}
              {importedCalibration && importedCalibration.Mismatch && (
                <>
                  <div className="EquipmentDialog-Subheading">Calibrated values imported to worksheet</div>
                  {EquipmentTypes[equipmentType]?.calibration?.(importedCalibration)}
                </>
              )}
            </>
          )}

          {status.OverdueCalibrations &&
            status.OverdueCalibrations.length > 0 &&
            status.OverdueCalibrations.map((calibrationTask: any) => (
              <CalibrationStatus
                key={calibrationTask.TaskId}
                name={calibrationTask.Name}
                lastPerformedDate={calibrationTask.PreviousCalibrationDate}
                dueDate={calibrationTask.CalibrationDueDate}
                overdue
              />
            ))}
        </div>

        {EquipmentTypes[equipmentType]?.isInterfacingSupported &&
          (equipment?.PortSettings?.IsValidPortSettings || equipment?.Driver) && (
            <div className="EquipmentDialog-Section">
              <div className="EquipmentDialog-Heading">Interfacing</div>
              {isWebSerialApiSupported() && isConnected && (
                <span>This equipment item is connected and ready to interface.</span>
              )}
              {!isConnected && equipment?.PortSettings?.IsValidPortSettings && equipment?.Driver && (
                <span>This equipment item can be connected to interface values.</span>
              )}
              {!equipment?.Driver && (
                <span>
                  This equipment is configured for interfacing but a QEST Web App driver has not been selected.
                </span>
              )}
              {!equipment?.PortSettings?.IsValidPortSettings && (
                <span>
                  This equipment cannot be connected as its COM port connection settings have not been configured
                  correctly.
                </span>
              )}
              <InterfaceConnectionButtons equipment={equipment} equipmentType={equipmentType} />
            </div>
          )}

        {isDevMode && (
          <div className="EquipmentDialog-Section">
            <div className="EquipmentDialog-Heading">Debug Tools</div>
            <div>
              <Link to={`/equipment/${equipment.Id}`} openInNewTab>
                Configure
              </Link>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export { EquipmentDialog };
